import { useContext, useEffect, type RefObject } from 'react'
import type { KeenSliderInstance } from 'keen-slider/react'
import { CarouselContext, type TypeCarouselContext } from '@shared/ui/Layout/Carousel/CarouselContext'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'

const AUTO_SCROLL_INTERVAL_MS = 5000
const USER_SCROLL_RESUME_MS = 200

type Params = {
  rootRef: RefObject<HTMLDivElement | null>
  controlsRef: RefObject<HTMLDivElement | null>
}

const isAtEnd = (instance: KeenSliderInstance) => {
  const details = instance.track?.details
  if (!details) return true
  return details.rel >= details.maxIdx
}

/**
 * Автопрокрутка фичер-карусели: порт логики с большой Афиши
 */
export const useFeatureAutoScroll = ({ rootRef, controlsRef }: Params) => {
  const [context] = useContext(CarouselContext) as TypeCarouselContext
  const { isMobile } = useClientResponsive()
  const instanceRef = context.instanceRef
  const maxIndex = context.details?.maxIndex

  useEffect(() => {
    const root = rootRef.current
    const controls = controlsRef.current
    const instance = instanceRef?.current as KeenSliderInstance | null | undefined
    const container = instance?.container

    if (!root || !instance || !container || maxIndex == null) return

    if (isMobile) {
      return bindMobileAutoScroll(instance, container)
    }

    return bindDesktopAutoScroll(instance, container, root, controls)
  }, [controlsRef, instanceRef, isMobile, maxIndex, rootRef])
}

const bindDesktopAutoScroll = (
  instance: KeenSliderInstance,
  container: HTMLElement,
  root: HTMLElement,
  controls: HTMLElement | null,
) => {
  let intervalId: number | null = null
  let isHovered = false

  const stopAnimationHandler = () => {
    if (intervalId !== null) {
      window.clearInterval(intervalId)
      intervalId = null
    }
  }

  const startAnimationHandler = () => {
    if (isHovered || intervalId !== null) return
    if (isAtEnd(instance)) return

    intervalId = window.setInterval(() => {
      if (isAtEnd(instance)) {
        stopAnimationHandler()
        return
      }
      instance.next()
    }, AUTO_SCROLL_INTERVAL_MS)
  }

  const onMouseEnter = () => {
    isHovered = true
    stopAnimationHandler()
  }

  const onMouseLeave = () => {
    isHovered = false
    startAnimationHandler()
  }

  const onControlClick = () => {
    stopAnimationHandler()
  }

  const onSlideChanged = () => {
    if (isAtEnd(instance)) {
      stopAnimationHandler()
      return
    }
    startAnimationHandler()
  }

  root.addEventListener('mouseenter', onMouseEnter)
  root.addEventListener('mouseleave', onMouseLeave)
  controls?.addEventListener('click', onControlClick)
  instance.on('slideChanged', onSlideChanged)

  const intersectionObserver = new IntersectionObserver(([{ isIntersecting }]) => {
    if (isIntersecting) {
      startAnimationHandler()
    } else {
      isHovered = false
      stopAnimationHandler()
    }
  })

  intersectionObserver.observe(container)
  startAnimationHandler()

  return () => {
    stopAnimationHandler()
    intersectionObserver.disconnect()
    instance.on('slideChanged', onSlideChanged, true)
    root.removeEventListener('mouseenter', onMouseEnter)
    root.removeEventListener('mouseleave', onMouseLeave)
    controls?.removeEventListener('click', onControlClick)
  }
}

const bindMobileAutoScroll = (instance: KeenSliderInstance, container: HTMLElement) => {
  let timeoutId: number | null = null
  let scrollTimerId: number | null = null

  const stopAnimationHandler = () => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  const onScroll = () => {
    stopAnimationHandler()
    if (!isAtEnd(instance)) {
      timeoutId = window.setTimeout(() => startAnimationHandler(), AUTO_SCROLL_INTERVAL_MS)
    }
  }

  const startAnimationHandler = () => {
    if (isAtEnd(instance)) {
      stopAnimationHandler()
      return
    }
    instance.next()
    onScroll()
  }

  const onUserScroll = () => {
    if (scrollTimerId !== null) {
      window.clearTimeout(scrollTimerId)
    }
    scrollTimerId = window.setTimeout(() => {
      scrollTimerId = null
      onScroll()
    }, USER_SCROLL_RESUME_MS)
  }

  container.addEventListener('touchstart', stopAnimationHandler)
  container.addEventListener('touchend', onUserScroll)

  const intersectionObserver = new IntersectionObserver(([{ isIntersecting }]) => {
    if (isIntersecting) {
      onScroll()
    } else {
      stopAnimationHandler()
    }
  })

  intersectionObserver.observe(container)

  return () => {
    stopAnimationHandler()
    if (scrollTimerId !== null) {
      window.clearTimeout(scrollTimerId)
    }
    intersectionObserver.disconnect()
    container.removeEventListener('touchstart', stopAnimationHandler)
    container.removeEventListener('touchend', onUserScroll)
  }
}
